import { Container, Row, Col, Button, Spinner, Tab, Tabs } from 'react-bootstrap'
import { StepHeader } from '../StepHeader/StepHeader'
import { StepperComponent } from '../../Components/StepperComponent/StepperComponent'
import { MigrationTracker } from '../../Components/MigrationTracker/MigrationTracker'
import { useNavigate, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { Check } from 'monday-ui-react-core/icons'
import { Icon } from 'monday-ui-react-core'
import config from '../../config'

import './Step6.scss'

export const Step6 = () => {
  const navigate = useNavigate()
  const { state } = useLocation()

  const [statusData, setStatusData] = useState()
  const [migrationCanceled, setMigrationCanceled] = useState(false)
  const [selectedMigrationId, setSelectedMigrationId] = useState(null)
  const [activeTab, setActiveTab] = useState('legacy')
  const [activeMigrations, setActiveMigrations] = useState([])

  useEffect(() => {
    // Clear previous migration data when component mounts
    setStatusData(undefined)
    setActiveMigrations([])
    
    let inProgressFlag = false
    const fetchInterval = setInterval(async () => {
      if (inProgressFlag) {
        return
      }
      inProgressFlag = true

      try {
        const currentStatusRes = await fetch(`${config.BACKEND_URL}/migrate/migrationStatus`, {
          headers: {
            'ngrok-skip-browser-warning': '69420',
            // secure: true,
          },
          credentials: 'include',
        })

        // Handle both 404 and 'no_migration' status
        if (currentStatusRes.status === 404) {
          console.log('No migration in progress (404)')
          clearInterval(fetchInterval)
          return
        }

        if (!currentStatusRes.ok) {
          // error happend on backend
          console.error('Error happend on backend')
        } else {
          const currentData = await currentStatusRes.json()
          setStatusData(currentData)
          
          // Fetch active migrations for progress tracking
          if (currentData.activeMigrations && currentData.activeMigrations.length > 0) {
            setActiveMigrations(currentData.activeMigrations)
          } else {
            setActiveMigrations([])
          }
          
          if (['done', 'cancel'].includes(currentData.status)) {
            clearInterval(fetchInterval)
          }
        }
      } catch (error) {
        console.error('Error happend while fetching migration status', error)
      }

      inProgressFlag = false
    }, 10000)

    return () => {
      clearInterval(fetchInterval)
    }
  }, [])

  return (
    <Container>
      <Row>
        <StepHeader />
      </Row>
      <Row>
        <Col>
          <StepperComponent currentStep={6} />
        </Col>
      </Row>
      <Row className={'d-flex mb-3 justify-content-center'}>
        <Col md={10}>
          {/* Overall Progress Summary - Compact */}
          {statusData && (
            <div className={'mb-3 p-2 bg-light rounded border'}>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>Overall:</strong> {statusData.completedProjects || 0} / {statusData.totalProjects || 0} projects
                  {statusData.failedProjects > 0 && (
                    <span className="text-danger ms-2">({statusData.failedProjects} failed)</span>
                  )}
                </div>
                {statusData.overallPercentage !== undefined && (
                  <div className="d-flex align-items-center">
                    <div className="progress me-2" style={{ height: '20px', width: '150px' }}>
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${statusData.overallPercentage}%` }}
                        aria-valuenow={statusData.overallPercentage}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      >
                        {statusData.overallPercentage}%
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Individual Project Progress - Main View */}
          {activeMigrations && activeMigrations.length > 0 && (
            <div className={'mb-4'}>
              <h5 className="mb-3">Migration Progress by Project</h5>
              {activeMigrations.map((migration, idx) => (
                <div key={migration.projectId} className={'mb-3 p-3 border rounded shadow-sm bg-white'}>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h6 className="mb-0">{migration.projectName}</h6>
                    <span className="badge bg-primary">{migration.percentageComplete}%</span>
                  </div>
                  
                  <div className="row g-2 mb-2">
                    <div className="col-6 col-md-3">
                      <div className="small text-muted">Tasks</div>
                      <div><strong>{migration.completedTasks}</strong> / {migration.totalTasks}</div>
                    </div>
                    <div className="col-6 col-md-3">
                      <div className="small text-muted">Issues</div>
                      <div><strong>{migration.completedIssues}</strong> / {migration.totalIssues}</div>
                    </div>
                    <div className="col-6 col-md-3">
                      <div className="small text-muted">Documents</div>
                      <div><strong>{migration.completedDocuments}</strong> / {migration.totalDocuments}</div>
                    </div>
                    {migration.totalUpdates > 0 && (
                      <div className="col-6 col-md-3">
                        <div className="small text-muted">Updates</div>
                        <div><strong>{migration.completedUpdates}</strong> / {migration.totalUpdates}</div>
                      </div>
                    )}
                  </div>
                  
                  <div className="progress" style={{ height: '25px' }}>
                    <div
                      className="progress-bar progress-bar-striped progress-bar-animated"
                      role="progressbar"
                      style={{ width: `${migration.percentageComplete}%` }}
                      aria-valuenow={migration.percentageComplete}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    >
                      {migration.percentageComplete}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-3"
          >
            <Tab eventKey="legacy" title="Detailed Log View">

              <div className={'mb-3'}>
                {`${statusData?.data?.length || '0'} projects migrated`.trim()}
              </div>
          <div className={'status-report'}>
            {(!statusData ||
              (statusData.data.length === 0 && statusData.status === 'in_progress')) && (
              <div className={'mb-3'}>{'Initialising migration data!'}</div>
            )}
            {statusData &&
              statusData.data.map((project, i) => (
                <ul style={{ marginTop: '20px' }} className="ps-4">
                  {project.project}{' '}
                  {i + 1 === statusData.data.length ? (
                    <>
                      {statusData.status === 'in_progress' ? (
                        <Spinner animation="border" role="status" size="sm" className="ms-1">
                          <span className="visually-hidden">Loading...</span>
                        </Spinner>
                      ) : (
                        <Icon
                          className="text-success ms-1"
                          iconType={Icon.type.SVG}
                          icon={Check}
                          iconSize={24}
                        />
                      )}
                    </>
                  ) : (
                    <Icon
                      className="text-success ms-1"
                      iconType={Icon.type.SVG}
                      icon={Check}
                      iconSize={24}
                    />
                  )}
                  {/* {statusData.status === 'done' && (
                    <Icon iconType={Icon.type.SVG} icon={Check} iconSize={24} />
                  )} */}
                  {
                    <>
                      {/* {TASK SECTION} */}
                      <ul
                        style={{ marginTop: '10px' }}
                      >{`Tasks: ${project.completedTasks}${project.totalTasks ? `/${project.totalTasks}` : ''}`}</ul>
                      <ul
                        style={{ marginTop: '5px' }}
                      >{`Failed Tasks: ${project?.failedTasks?.length} `}</ul>
                      {project.failedTasks.length > 0 && (
                        <ul style={{ marginTop: '5px' }}>
                          {project.failedTasks.map((task) => (
                            <div>
                              <ul style={{ color: 'red', marginRight: '30px', marginTop: '5px' }}>
                                {`Error Message: ${task}`}
                              </ul>
                            </div>
                          ))}
                        </ul>
                      )}
                      {/* {ISSUE SECTION} */}
                      <ul
                        style={{ marginTop: '10px' }}
                      >{`Issues: ${project.completedIssues}${project.totalIssues ? `/${project.totalIssues}` : ''}`}</ul>
                      <ul
                        style={{ marginTop: '5px' }}
                      >{`Failed Issues: ${project?.failedIssues?.length} `}</ul>
                      {project.failedIssues.length > 0 && (
                        <ul style={{ marginTop: '5px' }}>
                          {project.failedIssues.map((issue) => (
                            <div>
                              <ul style={{ color: 'red', marginRight: '30px', marginTop: '5px' }}>
                                {`Error Message: ${issue}`}
                              </ul>
                            </div>
                          ))}
                        </ul>
                      )}
                      {/* {DOCUMENT SECTION} */}
                      <ul
                        style={{ marginTop: '10px' }}
                      >{`Documents: ${project.completedDocuments}${project.totalDocuments ? `/${project.totalDocuments}` : ''}`}</ul>
                      <ul
                        style={{ marginTop: '5px' }}
                      >{`Failed Documents: ${project?.failedDocuments?.length} `}</ul>
                      {project.failedDocuments.length > 0 && (
                        <ul style={{ marginTop: '5px' }}>
                          {project.failedDocuments.map((document) => (
                            <div>
                              <ul style={{ color: 'red', marginRight: '30px', marginTop: '5px' }}>
                                {`Error Message: ${document}`}
                              </ul>
                            </div>
                          ))}
                        </ul>
                      )}
                      {/* {UPDATES SECTION} */}
                      {(project.completedUpdates > 0 || project.totalUpdates > 0 || (project.failedUpdates && project.failedUpdates.length > 0)) && (
                        <>
                          <ul
                            style={{ marginTop: '10px' }}
                          >{`Updates: ${project.completedUpdates || 0}${project.totalUpdates ? `/${project.totalUpdates}` : ''}`}</ul>
                          <ul
                            style={{ marginTop: '5px' }}
                          >{`Failed Updates: ${project?.failedUpdates?.length || 0} `}</ul>
                          {project.failedUpdates && project.failedUpdates.length > 0 && (
                            <ul style={{ marginTop: '5px' }}>
                              {project.failedUpdates.map((update) => (
                                <div>
                                  <ul style={{ color: 'red', marginRight: '30px', marginTop: '5px' }}>
                                    {`Error Message: ${update}`}
                                  </ul>
                                </div>
                              ))}
                            </ul>
                          )}
                        </>
                      )}
                    </>
                  }
                </ul>
              ))}
            <div className="ms-4">
              {statusData?.status === 'done' && (
                <p>
                  <b>Migration finished</b>
                  <Icon
                    className="text-success ms-2"
                    iconType={Icon.type.SVG}
                    icon={Check}
                    iconSize={24}
                  />
                </p>
              )}
              {statusData?.status === 'done_with_errors' && (
                <div>
                  <p>
                    <b className="text-warning">Migration completed with errors</b>
                  </p>
                  <p className="text-muted small">
                    {statusData.failedProjects || 0} project(s) failed to migrate
                  </p>
                  {statusData?.failedProjectsList && statusData.failedProjectsList.length > 0 && (
                    <div className="mt-3">
                      <h6 className="text-danger">Failed Projects:</h6>
                      <ul className="list-unstyled">
                        {statusData.failedProjectsList.map((failedProj, idx) => (
                          <li key={idx} className="mb-2 p-2 border-start border-danger border-3 bg-light">
                            <div><strong>{failedProj.projectName}</strong></div>
                            <div className="small text-muted">ID: {failedProj.projectId}</div>
                            <div className="small text-danger">Stage: {failedProj.failureStage}</div>
                            <div className="small text-danger">Error: {failedProj.error}</div>
                            <div className="small text-muted">Time: {new Date(failedProj.timestamp).toLocaleString()}</div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              {statusData?.status === 'cancel' && (
                <p>
                  <b className="text-danger">Migration cancelled</b>
                </p>
              )}
              </div>
            </div>
            </Tab>
          </Tabs>
        </Col>
      </Row>
      <Row className={'mt-5'}>
        <Col xs={11} className={'d-flex justify-content-end'}>
          {/* <Button
            className={'me-5 backButton'}
            disabled={!statusData?.status !== 'done' && !statusData?.status !== 'cancel'}
            onClick={() => navigate('/step5')}
          >
            Back
          </Button> */}
          {(statusData?.status && statusData?.status === 'done') ||
          (statusData?.status && statusData?.status === 'done_with_errors') ||
          (statusData?.status && statusData?.status === 'cancel') ? (
            <Button
              className={'cancelButton'}
              onClick={async () => {
                const resp = await fetch(`${config.BACKEND_URL}/migrate/migrationStatus`, {
                  method: 'DELETE',
                  headers: {
                    'ngrok-skip-browser-warning': '69420',
                    // secure: true,
                  },
                  credentials: 'include',
                })
                if (resp.ok) {
                  localStorage.clear()
                  navigate('/step3')
                }
              }}
            >
              Finish
            </Button>
          ) : (
            <Button
              className={'cancelButton'}
              onClick={async () => {
                setMigrationCanceled(true)
                await fetch(`${config.BACKEND_URL}/migrate/abort`, {
                  method: 'POST',
                  credentials: 'include',
                  // secure: true,
                })
              }}
            >
              {migrationCanceled ? (
                <Spinner
                  animation="border"
                  variant="dark"
                  style={{ width: '20px', height: '20px' }}
                />
              ) : (
                'Cancel'
              )}
            </Button>
          )}
        </Col>
      </Row>
    </Container>
  )
}
